import { useState } from "react"
import { ChevronDown, ChevronRight } from "lucide-react"
import { cn } from "@/lib/cn"
import type {
  ActionPayload,
  Cell,
  CellType,
  TableBlock,
  TableColumn,
  TableRow,
  Tone,
} from "../types"
import { TextCell } from "../cells/text-cell"
import { NumberCell } from "../cells/number-cell"
import { MoneyCell } from "../cells/money-cell"
import { DateCell } from "../cells/date-cell"
import { DateTimeCell } from "../cells/datetime-cell"
import { DurationCell } from "../cells/duration-cell"
import { BadgeCell } from "../cells/badge-cell"
import { LinkCell } from "../cells/link-cell"
import { ActionCell } from "../cells/action-cell"
import { RedactedCell } from "../cells/redacted-cell"

type Props = {
  block: TableBlock
  onAction: (action: ActionPayload, refresh?: string[]) => Promise<unknown>
}

// Tabular data block. Per-column `priority` (1/2/3) drives container-query
// hiding (priority 2 hidden at xs; priority 3 hidden at xs+sm). Hidden
// cells become a row-level expand affordance (chevron + stacked
// label/value list of the dropped cells).
export function TableBlockView({ block, onAction }: Props) {
  const columns = Array.isArray(block.columns) ? block.columns : []
  const allRows = Array.isArray(block.rows) ? block.rows : []
  const max = block.max_rows && block.max_rows > 0 ? block.max_rows : 5
  const rows = allRows.slice(0, max)

  if (rows.length === 0) {
    return (
      <div className="rounded-control border border-dashed border-border bg-app-panel2/40 px-3 py-3 text-center text-xs text-app-subtle">
        {block.empty_text || "No data."}
      </div>
    )
  }

  return (
    <div className="overflow-x-auto rounded-card border border-border">
      <table
        className={cn(
          "valet-uidoc-table w-full text-sm",
          block.dense && "valet-uidoc-table-dense",
        )}
      >
        <thead className="bg-app-panel2 text-[11px] uppercase tracking-wide text-app-subtle">
          <tr>
            <th aria-hidden="true" className="valet-uidoc-table-expander w-6" />
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={cn(
                  "px-2 py-2 font-medium",
                  col.align === "right"  && "text-right",
                  col.align === "center" && "text-center",
                  col.priority === 2 && "valet-uidoc-table-pri-2",
                  col.priority === 3 && "valet-uidoc-table-pri-3",
                )}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <Row key={idx} row={row} columns={columns} onAction={onAction} />
          ))}
        </tbody>
      </table>
      {allRows.length > rows.length && (
        <div className="border-t border-border bg-app-panel2/40 px-3 py-1.5 text-center text-[11px] text-app-subtle">
          Showing {rows.length} of {allRows.length}
        </div>
      )}
    </div>
  )
}

function Row({
  row, columns, onAction,
}: {
  row: TableRow
  columns: TableColumn[]
  onAction: (action: ActionPayload, refresh?: string[]) => Promise<unknown>
}) {
  const [open, setOpen] = useState(false)
  const hiddenCols = columns.filter((c) => (c.priority ?? 1) > 1)
  const hasHidden = hiddenCols.length > 0

  return (
    <>
      <tr className="border-t border-border align-top">
        <td className="valet-uidoc-table-expander w-6 px-1 py-2">
          {hasHidden && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="valet-uidoc-table-expander-btn flex h-6 w-6 items-center justify-center rounded-control text-app-muted hover:text-app-text"
              aria-label={open ? "Collapse row" : "Expand row"}
              aria-expanded={open}
            >
              {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          )}
        </td>
        {columns.map((col) => (
          <td
            key={col.key}
            className={cn(
              "px-2 py-2 align-top",
              col.priority === 2 && "valet-uidoc-table-pri-2",
              col.priority === 3 && "valet-uidoc-table-pri-3",
            )}
          >
            <RenderCell value={row[col.key]} column={col} onAction={onAction} />
          </td>
        ))}
      </tr>
      {open && hasHidden && (
        <tr className="border-t border-border bg-app-panel2/30">
          <td colSpan={columns.length + 1} className="px-3 py-2">
            <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs">
              {hiddenCols.map((col) => (
                <div key={col.key} className="contents">
                  <dt className="text-app-subtle">{col.label}</dt>
                  <dd className="min-w-0 text-app-text">
                    <RenderCell value={row[col.key]} column={col} onAction={onAction} />
                  </dd>
                </div>
              ))}
            </dl>
          </td>
        </tr>
      )}
    </>
  )
}

function RenderCell({
  value, column, onAction,
}: {
  value: unknown
  column: TableColumn
  onAction: (action: ActionPayload, refresh?: string[]) => Promise<unknown>
}) {
  // Normalize: pre-tagged cell objects pass through; scalars defer to
  // the column's declared type (default "text").
  const cell = normalizeCell(value, column.type ?? "text")

  switch (cell.type) {
    case "redacted":   return <RedactedCell />
    case "number":     return <NumberCell value={cell.value} align={column.align ?? "right"} />
    case "money_usd":  return <MoneyCell value={cell.value} currency={cell.currency} align={column.align ?? "right"} />
    case "date":       return <DateCell value={cell.value} align={column.align ?? "left"} />
    case "datetime":   return <DateTimeCell value={cell.value} align={column.align ?? "left"} />
    case "duration_ms":return <DurationCell value={cell.value} align={column.align ?? "right"} />
    case "badge":      return <BadgeCell value={cell.value} />
    case "link":       return <LinkCell value={cell.value} />
    case "action":     return <ActionCell value={cell.value} onAction={onAction} />
    case "raw":        return <span className="text-app-subtle">&lt;invalid&gt;</span>
    case "text":
    default:           return <TextCell value={cell.value} align={column.align ?? "left"} />
  }
}

// Coerce a free-form `row[col.key]` into a typed Cell. The wire format
// permits scalars (string/number/null) for non-action columns; tagged
// objects (`{type: "badge", value: {…}}`, `{label, href}`, etc.) take
// precedence. Mismatched shapes degrade to `<invalid>` rather than
// throwing.
function normalizeCell(raw: unknown, columnType: CellType): Cell {
  if (columnType === "redacted") return { type: "redacted", value: raw }

  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>

    // Pre-tagged cell shape {type: "...", value: ...}
    if (typeof obj.type === "string") {
      return tagged(obj, columnType)
    }

    // Bare structured payloads where the column type implies the shape.
    if (columnType === "badge" && (typeof obj.label === "string")) {
      return { type: "badge", value: { label: obj.label as string, tone: obj.tone as Tone | undefined } }
    }
    if (columnType === "link" && typeof obj.label === "string" && typeof obj.href === "string") {
      return { type: "link", value: { label: obj.label as string, href: obj.href as string } }
    }
    if (columnType === "action" && typeof obj.label === "string" && typeof obj.path === "string" && typeof obj.method === "string") {
      return { type: "action", value: obj as unknown as ActionPayload }
    }

    return { type: "raw", value: null }
  }

  // Scalar
  switch (columnType) {
    case "number":
    case "duration_ms":
    case "money_usd":
      return columnType === "duration_ms"
        ? { type: "duration_ms", value: typeof raw === "number" ? raw : null }
        : columnType === "money_usd"
        ? { type: "money_usd", value: typeof raw === "number" ? raw : null }
        : { type: "number", value: typeof raw === "number" ? raw : null }
    case "date":
      return { type: "date", value: typeof raw === "string" ? raw : null }
    case "datetime":
      return { type: "datetime", value: typeof raw === "string" ? raw : null }
    case "badge":
    case "link":
    case "action":
      return { type: "raw", value: null }
    default:
      return {
        type: "text",
        value: raw === null || raw === undefined ? null : String(raw),
      }
  }
}

function tagged(obj: Record<string, unknown>, _columnType: CellType): Cell {
  const type = obj.type as string
  const value = obj.value
  switch (type) {
    case "text":        return { type: "text", value: typeof value === "string" || typeof value === "number" ? String(value) : null }
    case "number":      return { type: "number", value: typeof value === "number" ? value : null }
    case "money_usd":   return { type: "money_usd", value: typeof value === "number" ? value : null, currency: typeof obj.currency === "string" ? obj.currency : "USD" }
    case "date":        return { type: "date", value: typeof value === "string" ? value : null }
    case "datetime":    return { type: "datetime", value: typeof value === "string" ? value : null }
    case "duration_ms": return { type: "duration_ms", value: typeof value === "number" ? value : null }
    case "badge":       return { type: "badge", value: badgeValue(value) }
    case "link":        return { type: "link", value: linkValue(value) }
    case "action":      return { type: "action", value: actionValue(value) }
    case "redacted":    return { type: "redacted", value: value }
    default:            return { type: "raw", value: null }
  }
}

function badgeValue(value: unknown): { label: string; tone?: Tone } | null {
  if (!value || typeof value !== "object") return null
  const v = value as Record<string, unknown>
  if (typeof v.label !== "string") return null
  return { label: v.label, tone: typeof v.tone === "string" ? (v.tone as Tone) : undefined }
}

function linkValue(value: unknown): { label: string; href: string } | null {
  if (!value || typeof value !== "object") return null
  const v = value as Record<string, unknown>
  if (typeof v.label !== "string" || typeof v.href !== "string") return null
  return { label: v.label, href: v.href }
}

function actionValue(value: unknown): ActionPayload | null {
  if (!value || typeof value !== "object") return null
  const v = value as Record<string, unknown>
  if (typeof v.label !== "string" || typeof v.path !== "string" || typeof v.method !== "string") return null
  return v as unknown as ActionPayload
}
